import React, { useMemo, useRef } from 'react';
import { IconFormIcons, Plus16, Target } from 'shared/assets/icons';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import { IconButton, Sizes } from 'shared/components/ui/icon-button';
import { TimeLeft } from 'shared/components/ui/time-left';
import { useOpenCreateTask } from 'shared/contexts/task-detail';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Paths } from 'shared/routes';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import * as Styled from './favorite-goal.style';

export type FavoriteGoalProps = {
  goal: Goal;
  goals: Goal[];
  lifeAreas: LifeArea[];
  currentPath?: string;
  isMoving?: boolean;
};

export const FavoriteGoal: React.FC<FavoriteGoalProps> = ({
  goal,
  goals,
  lifeAreas,
  currentPath,
  isMoving,
}) => {
  const containerRef = useRef<HTMLAnchorElement>(null);
  const url = replaceUrlParams(Paths.GoalDetail, { id: goal.id });
  const isActive = !!currentPath && currentPath.includes(url);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const openCreateTask = useOpenCreateTask();

  const onCreateTask = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openCreateTask({ goalId: goal.id });
  };

  const { end, percentage } = useMemo(() => {
    const { start, current, end } = getGoalCompletion(goal, {
      goals,
    });
    const percentage = getCompletionPercentage({ start, current, end });

    return {
      start,
      current,
      end,
      percentage,
    };
  }, [goal, goals]);

  const color = useMemo(
    () => getLinkedItemColor(goal.id, { goals, lifeAreas }),
    [goal.id, goals, lifeAreas],
  );

  return (
    <>
      <Styled.Link
        to={url}
        $active={isActive}
        as={goal.frozenAt ? 'span' : undefined}
        $isMoving={!!isMoving}
        $isFrozen={!!goal.frozenAt}
        ref={containerRef}
        onContextMenu={!goal.frozenAt ? openMenu : undefined}
      >
        <Styled.IconContainer $active={isActive} $color={color}>
          {end === Infinity || !!goal.completedAt ? (
            <Icon
              icon={goal.iconName ? IconFormIcons[goal.iconName] : Target}
            />
          ) : (
            <CircleProgress percentage={percentage} hidePercentage isSmall />
          )}
        </Styled.IconContainer>

        <Styled.Title $completed={!!goal.completedAt}>{goal.name}</Styled.Title>

        {!!goal.deadline && !goal.completedAt && (
          <Styled.TimeLeftContainer $active={isActive}>
            <TimeLeft date={goal.deadline!} isShort />
          </Styled.TimeLeftContainer>
        )}

        <Styled.IconButtonContainer>
          <IconButton onClick={onCreateTask} icon={Plus16} size={Sizes.Small} />
        </Styled.IconButtonContainer>

        {menuOpen && (
          <GoalContextMenu
            id={goal.id}
            onClose={closeMenu}
            referenceElement={containerRef}
          />
        )}
      </Styled.Link>
    </>
  );
};
